/**
 * Friction surveys, finished work recorded afterwards, and a site's own model
 * figures — as the platform holds them.
 *
 * Imports nothing: the write mappings (`demo/platform/writes/friction.ts`)
 * sit in the entry chunk and use the helpers at the bottom.
 */

/** One run of a survey: a lap in one direction, on one lane, as series along the line. */
export interface FrictionRun {
  direction: string;
  lap: number;
  offsetM?: number;
  label?: string;
  /** chainage along the line from its origin (THR 17 on RWY-17-35), metres */
  fromM: number[];
  toM: (number | null)[];
  value: number[];
}

export interface FrictionLevels {
  design?: number;
  planning?: number;
  minimum?: number;
}

/** A survey on the platform, grouped from its runs. */
export interface FrictionSurvey {
  surveyId: string;
  /** "manual": a touchdown / midpoint / rollout spot test for one end */
  kind: "survey" | "manual";
  observedAt: string;
  observedOn: string;
  reference?: string;
  referenceLengthM?: number;
  /** the designator the line's chainage runs from ("17" on RWY-17-35) */
  forward?: string;
  device?: string;
  speedKmh?: number;
  /** the survey's own levels; absent on a survey loaded before they were kept */
  levels?: FrictionLevels;
  header: Record<string, unknown>;
  importedBy?: string;
  importedAt?: string;
  runs: FrictionRun[];
}

/** A whole survey to load: every lap and lane in one go. */
export interface NewFrictionSurvey {
  surveyId: string;
  kind?: "survey" | "manual";
  referenceKey: string;
  measurement?: string;
  observedAt: string;
  device?: string;
  speedKmh?: number;
  levels?: FrictionLevels;
  header?: Record<string, unknown>;
  /** "as_flown": each run from its own threshold; the platform turns the far end round */
  chainage?: "reference" | "as_flown";
  runs: {
    direction: string;
    lap: number;
    offsetM?: number;
    label?: string;
    segments: { fromM: number; toM: number; value: number }[];
  }[];
}

export interface FrictionSurveyAck {
  surveyId: string;
  runs: number;
  segments: number;
  documents: string[];
  importedAt?: string;
  importedBy?: string;
  /** the same request sent again; the first answer came back */
  replayed?: boolean;
}

/** Work already done, recorded as done on the day it was done. */
export interface CompletedWorkInput {
  siteId: string;
  title: string;
  /** what kind of work, e.g. "rubber_removal" — what a model counts days from */
  activity: string;
  /** YYYY-MM-DD */
  completedOn: string;
  /** the record's own id: the same record sent twice stays one order */
  clientId: string;
  kind?: string;
  priority?: number;
  description?: string;
  zoneCode?: string;
  /** used when `zoneCode` is not mapped at the site */
  zoneFallback?: string;
  assetTag?: string;
  attrs: Record<string, unknown>;
}

export interface CompletedWorkAck {
  id: string;
  number: string;
  status: string;
  /** false: this record was already held */
  created: boolean;
  completedAt?: string;
  zoneCode?: string;
}

export interface CompletedWorkPatch {
  attrs?: Record<string, unknown>;
  completedOn?: string;
  note?: string;
}

export interface CompletedWorkAmended {
  id: string;
  number: string;
  status: string;
  changed: boolean;
  fields: string[];
}

/** A site's own figures for a model, and who saved them. */
export interface ModelParams {
  model: string;
  /** what the platform's model reads, in its own units */
  params: Record<string, number>;
  /** what people entered on the screen, in the screen's units */
  inputs: unknown;
  zones: Record<string, Record<string, number>>;
  note?: string;
  savedBy?: string;
  savedAt?: string;
  zonesApplied?: string[];
  zonesMissing?: string[];
}

export interface ModelParamsInput {
  params: Record<string, number>;
  inputs?: unknown;
  zones?: Record<string, Record<string, number>>;
  note?: string;
}

/* ── the board's frame and the platform's ──────────────────────────────── */

/** "17/35" → "RWY-17-35", the line a runway's surveys are kept against. */
export const runwayReference = (runway: string): string => `RWY-${runway.replace("/", "-")}`;

/** "17/35" → ["17", "35"]. */
export const designators = (runway: string): [string, string] => {
  const [a, b] = runway.split("/");
  return [a ?? "", b ?? ""];
};

/** The touchdown third a removal at one end is recorded against, as the platform names it. */
export function touchdownZone(runway: string, end: string): string | undefined {
  const [first, second] = designators(runway);
  if (end === first) return `${runwayReference(runway)}-TDZ-A`;
  if (end === second) return `${runwayReference(runway)}-TDZ-B`;
  return undefined;
}

/**
 * A run's readings in the frame it was flown in: metres from the threshold
 * it started at, the end of each segment — the board's `d`. The far end's
 * runs are turned back round on the line's own length.
 */
export function asFlown(run: FrictionRun, forward: string | undefined, lengthM: number | undefined): { d: number[]; value: number[] } {
  const far = forward !== undefined && run.direction !== forward && lengthM !== undefined;
  const pts = run.fromM.map((from, i) => {
    const to = run.toM[i] ?? from;
    const d = far ? (lengthM as number) - from : to;
    return { d: Math.round(d * 100) / 100, v: run.value[i] };
  });
  pts.sort((a, b) => a.d - b.d);
  return { d: pts.map((p) => p.d), value: pts.map((p) => p.v) };
}
